import { type InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import type { IPaginationQueryParams } from "@/features/common/types";
import { queryKeyMap } from "@/features/query-client";

import type { IGetLenderCompanyParams } from "../api";
import { getMyLenderCompanies } from "../api/company-api";

type ResponseType = Awaited<ReturnType<typeof getMyLenderCompanies>>;

interface IOptions {
  enabled?: boolean;
  staleTime?: number;
}

export const useGetMyLenderCompaniesQuery = (
  input?: IGetLenderCompanyParams,
  options?: IOptions,
) => {
  const DefaultLimit = input?.limit ?? 10;
  const { search } = input ?? {};

  return useInfiniteQuery<
    ResponseType,
    AxiosError,
    InfiniteData<ResponseType>,
    ReturnType<typeof queryKeyMap.getMyLenderCompanies>,
    IPaginationQueryParams
  >({
    queryKey: queryKeyMap.getMyLenderCompanies(search),
    queryFn: async ({ pageParam }) => {
      return await getMyLenderCompanies({
        ...pageParam,
        search,
      });
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.count < (lastPageParam.offset ?? DefaultLimit)) {
        return undefined;
      }

      return {
        limit: DefaultLimit,
        offset: (lastPageParam.offset ?? 0) + DefaultLimit,
      };
    },
    initialPageParam: {
      limit: DefaultLimit,
      offset: 0,
    },
    ...options,
  });
};
